import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Divider, Flex } from 'antd';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Padding } from '../../common/components';
import { FormCheckbox, FormField, FormRadioGroup } from '../../common/form';
import { useSystemClassifierItemById } from '../../configuration';
import translations from '../translations/CalendarEdit.translations.json';
import { DataObjectGroupType, DataObjectView, Frequency } from '../types';
import { ExecutionCalendar } from './ExecutionCalendar';
import { ExecutionPeriodsEdit } from './ExecutionPeriods';
import { LoadingSection } from './LoadingSection';
import { SampleLoadingFields } from './SampleLoadingFields';
import { ExecutionMethod } from './types';
import { FormTimePicker } from '../../common/form/input/FormTimePicker';
import { ExecutionPublicationsEditTable } from './ExecutionPublicationsEditTable';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { DataObjectValidFormDataType } from '../schema';

const { addTranslations, useTranslation } = i18nNsFactory(
  'execution-calendar-edit',
);
addTranslations(translations);

export interface CalendarEditProps {
  dataObjectView: DataObjectView;
}

export function CalendarEdit({ dataObjectView }: CalendarEditProps) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const isInput = dataObjectView.dataObjectGroupType === DataObjectGroupType.I;
  const isOutput = dataObjectView.dataObjectGroupType === DataObjectGroupType.O;
  const executionMethodCode = useExecutionMethodCode();
  const frequency = useFrequency();
  const executionPublications = useWatch({
    name: 'execution.executionPublications',
  });
  const [{ allVersions, versionId }] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const [groupData] = DataObjectDescriptionVersionStore.useValue('groupData');
  const statusCode = useWatch<DataObjectValidFormDataType, 'statusCode'>({
    name: 'statusCode',
  });

  const allVersionsAreDrafts = useMemo(() => {
    if (groupData) {
      return !groupData.hasAnyNonDraftDataObjectDescriptionVersions;
    } else
      return (
        allVersions
          .filter(({ id }) => id !== versionId)
          .every((version) => version.statusCode === 'DRAFT') &&
        statusCode === 'DRAFT'
      );
  }, [allVersions, statusCode, versionId, groupData]);

  const loadSampleOptions = useMemo(
    () => [
      {
        value: false,
        label: t('loadSample.no'),
      },
      {
        value: true,
        label: t('loadSample.yes'),
      },
    ],
    [t],
  );
  const onChange = useCallback(
    (e: RadioChangeEvent) => {
      const loadSample = e.target.value as boolean;
      let sampleLoadingDays: number | null = null;
      let sampleLoadingTime: string | null = null;
      if (loadSample) {
        sampleLoadingDays = 0;
        sampleLoadingTime = '00:00';
      }

      setValue('execution.sampleLoadingDays', sampleLoadingDays, {
        shouldDirty: true,
      });
      setValue('execution.sampleLoadingTime', sampleLoadingTime, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  return (
    <ContentWrapper left right>
      {isInput && (
        <>
          <SectionWrapper gap="large">
            <FormField name="execution.autoLoadingPaused">
              <FormCheckbox name="execution.autoLoadingPaused">
                {t('autoLoadingPaused.label')}
              </FormCheckbox>
            </FormField>
          </SectionWrapper>
          <SectionDivider orientation="left">
            {t('section.sample')}
          </SectionDivider>
          <SectionWrapper gap="large">
            <FormField
              name="execution.loadSample"
              label={t('loadSample.label')}
              required
            >
              <FormRadioGroup
                name="execution.loadSample"
                onChange={onChange}
                options={loadSampleOptions}
                disabled={!allVersionsAreDrafts}
              />
            </FormField>
            <SampleLoadingFields />
          </SectionWrapper>
        </>
      )}
      <SectionDivider orientation="left">{t('section.method')}</SectionDivider>
      <SectionWrapper gap="large" wrap="wrap">
        <LoadingSection
          groupType={dataObjectView.dataObjectGroupType}
          executionMethod={executionMethodCode}
          isInput={isInput}
        />
      </SectionWrapper>
      {executionMethodCode === ExecutionMethod.CALENDAR && (
        <SectionWrapper gap="large">
          <ExecutionCalendar
            isInput={isInput}
            groupType={dataObjectView.dataObjectGroupType}
            frequency={frequency}
          />
        </SectionWrapper>
      )}
      {executionMethodCode === ExecutionMethod.PERIODIC && (
        <SectionWrapper gap="large">
          <ExecutionPeriodsEdit
            groupType={dataObjectView.dataObjectGroupType}
            isInput={isInput}
          />
        </SectionWrapper>
      )}
      {isOutput && (
        <>
          <SectionDivider orientation="left">
            {t('section.publish')}
          </SectionDivider>
          <SectionWrapper gap="large" wrap="wrap">
            <FormField
              name="execution.publicationTime"
              label={t('publicationTime.label')}
              required={executionPublications.length > 0}
            >
              <FormTimePicker
                name="execution.publicationTime"
                timeFormat="HH:mm"
              />
            </FormField>
          </SectionWrapper>
          <SectionWrapper gap="large">
            <ExecutionPublicationsEditTable frequency={frequency} />
          </SectionWrapper>
        </>
      )}
    </ContentWrapper>
  );
}

function useExecutionMethodCode() {
  const executionMethodId: number = useWatch({
    name: 'execution.executionMethodId',
  });
  return useSystemClassifierItemById('EXECUTION_METHOD', executionMethodId)
    ?.code;
}

function useFrequency() {
  const frequencyId: number = useWatch({
    name: 'frequencyId',
  });
  return useSystemClassifierItemById('FREQUENCY', frequencyId)?.code as
    | Frequency
    | undefined;
}

const ContentWrapper = styled(Padding)`
  width: 100%;
`;

const SectionDivider = styled(Divider)`
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const SectionWrapper = styled(Flex)`
  .ant-radio-group {
    margin-top: 6px;
  }
  padding: 24px 0;
`;
